/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * Maakt een element versleepbaar binnen zijn ouder, met `left`/`top`. De ng21-tegenhanger van de
 * AngularJS-directive `dl-draggable` (golf 4e): dezelfde beginpositie-opties en hetzelfde
 * binnen-de-grenzen-houden, maar met pointer-events in plaats van zes jQuery-bindingen, en met een
 * `ResizeObserver` op de ouder in plaats van een luisteraar op `window.resize` en de ng1-broadcast
 * `model.resized`.
 *
 * Gebruik:
 *   <div dlSleepbaar greep=".mijn-titelbalk" [startTop]="60" [startRechts]="62" binnenGrenzen>
 */
import {Directive, ElementRef, inject, input, OnDestroy, OnInit} from '@angular/core';

@Directive({
    selector: '[dlSleepbaar]'
})
export class DlSleepbaarDirective implements OnInit, OnDestroy {

    /**
     * De selector van het handvat binnen dit element; zonder handvat sleept het hele element.
     */
    readonly greep = input('');

    /**
     * Beginpositie: de afstand tot de bovenkant van de ouder, in pixels.
     */
    readonly startTop = input<number | null>(null);

    /**
     * Beginpositie: de afstand tot de rechterkant van de ouder, in pixels. Wordt omgerekend naar
     * `left`, dus de breedte van het element moet bekend zijn — vandaar dat het pas gebeurt als het
     * element in beeld staat.
     */
    readonly startRechts = input<number | null>(null);

    /**
     * Of het element bij een verandering van de ouder binnen zijn grenzen gehouden wordt.
     */
    readonly binnenGrenzen = input(false, {transform: (waarde: boolean | string) => waarde !== false});

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    /** De huidige plek, in pixels binnen de ouder. */
    private x = 0;
    private y = 0;

    /** Het verschil tussen de aanwijzer en de linkerbovenhoek bij het begin van de sleep. */
    private greepX = 0;
    private greepY = 0;

    /** Of de beginpositie al uitgerekend is; dat kan pas als het element in beeld staat. */
    private staatGoed = false;

    /** Houdt de ouder in de gaten: een andere maat vraagt om een nieuwe begrenzing. */
    private maatwacht: ResizeObserver | null = null;

    /** De luisteraars die tijdens een sleep aan het document hangen. */
    private readonly beweeg = (gebeurtenis: PointerEvent) => this.sleep(gebeurtenis);
    private readonly laatLos = () => this.eindigSleep();

    ngOnInit(): void {
        const element = this.host.nativeElement;
        element.addEventListener('pointerdown', gebeurtenis => this.beginSleep(gebeurtenis as PointerEvent));
        //de ouder kan van maat veranderen door de splitter, een simulatie die opengaat of een ander
        //venster; dan moet het element binnen beeld blijven. De eerste melding is de huidige maat en is
        //het moment waarop de beginpositie uitgerekend kan worden
        this.maatwacht = new ResizeObserver(() => this.naMaatwijziging());
        const ouder = this.ouder();
        if (ouder) {
            this.maatwacht.observe(ouder);
        }
    }

    ngOnDestroy(): void {
        this.maatwacht?.disconnect();
        this.maatwacht = null;
        this.stopLuisteren();
    }

    /**
     * De ouder waarbinnen gesleept wordt.
     */
    private ouder(): HTMLElement | null {
        const element = this.host.nativeElement;
        return (element.offsetParent as HTMLElement | null) ?? element.parentElement;
    }

    /**
     * Begint een sleep, tenzij de aanwijzer buiten het handvat of op een bedienbaar element staat.
     */
    private beginSleep(gebeurtenis: PointerEvent): void {
        const doel = gebeurtenis.target as HTMLElement;
        if (['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT'].includes(doel.tagName)) {
            return;
        }
        const selector = this.greep();
        if (selector && !doel.closest(selector)) {
            return;
        }
        gebeurtenis.preventDefault();
        this.greepX = gebeurtenis.clientX - this.x;
        this.greepY = gebeurtenis.clientY - this.y;
        document.addEventListener('pointermove', this.beweeg);
        document.addEventListener('pointerup', this.laatLos);
        document.addEventListener('pointercancel', this.laatLos);
    }

    /**
     * Verplaatst het element met de aanwijzer mee.
     */
    private sleep(gebeurtenis: PointerEvent): void {
        this.x = gebeurtenis.clientX - this.greepX;
        this.y = gebeurtenis.clientY - this.greepY;
        this.zetPositie();
    }

    /**
     * Rondt de sleep af.
     */
    private eindigSleep(): void {
        this.stopLuisteren();
        if (this.binnenGrenzen()) {
            this.houdBinnenGrenzen();
        }
    }

    /**
     * Haalt de sleepluisteraars van het document.
     */
    private stopLuisteren(): void {
        document.removeEventListener('pointermove', this.beweeg);
        document.removeEventListener('pointerup', this.laatLos);
        document.removeEventListener('pointercancel', this.laatLos);
    }

    /**
     * Schrijft de plek naar de css van het element.
     */
    private zetPositie(): void {
        const stijl = this.host.nativeElement.style;
        stijl.position = 'absolute';
        stijl.left = `${this.x}px`;
        stijl.top = `${this.y}px`;
    }

    /**
     * De ouder of het element veranderde van maat: de eerste keer de beginpositie uitrekenen, daarna
     * zorgen dat het element binnen beeld blijft.
     */
    private naMaatwijziging(): void {
        if (!this.staatGoed) {
            this.zetBeginpositie();
            return;
        }
        if (this.binnenGrenzen()) {
            this.houdBinnenGrenzen();
        }
    }

    /**
     * Rekent de beginpositie uit, zodra het element én zijn ouder een maat hebben.
     */
    private zetBeginpositie(): void {
        const element = this.host.nativeElement;
        const ouder = this.ouder();
        if (!ouder || !element.offsetWidth || !ouder.offsetWidth) {
            return; //nog niet in beeld
        }
        const top = this.startTop();
        const rechts = this.startRechts();
        if (top === null && rechts === null) {
            this.staatGoed = true;
            return; //geen beginpositie gevraagd
        }
        this.y = top ?? 0;
        this.x = rechts === null ? 0 : ouder.offsetWidth - element.offsetWidth - rechts;
        this.staatGoed = true;
        this.zetPositie();
    }

    /**
     * Corrigeert de plek zodat het element volledig binnen zijn ouder valt.
     */
    private houdBinnenGrenzen(): void {
        const element = this.host.nativeElement;
        const ouder = this.ouder();
        if (!ouder) {
            return;
        }
        this.x = Math.max(0, Math.min(this.x, ouder.offsetWidth - element.offsetWidth));
        this.y = Math.max(0, Math.min(this.y, ouder.offsetHeight - element.offsetHeight));
        this.zetPositie();
    }
}
